import {
  addToast,
  Button,
  ErrorToast,
  SuccessToast,
} from '@otter-ai/den-components';
import { GoogleIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { FormattedText } from '@/shared/components/FormattedText';
import { useApplications } from '@/shared/hooks/useApplications';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { descriptionFormatRules } from '../../shared/utils/text';
import { AppRowLayout } from '../components/AppRowLayout';
import { useDisconnectApplication } from '../hooks/useDisconnectApplication';
import { useGmailOAuth } from '../hooks/useGmailOAuth';
import { GMAIL_TEST_IDS } from '../test-ids/gmail';
import { DisconnectedButton } from './DisconnectedButton';

/**
 * The row for Gmail on the apps settings page.
 */
const APP_NAME = 'google-mcp';

export function GmailAppRow() {
  const t = useTranslations('settings:integrations.apps.gmail');
  const tCommon = useTranslations('settings:integrations');
  const { isApplicationConnected, isLoading: isLoadingStatus } =
    useApplications();
  const { mutateAsync: disconnectApp, isPending: isDisconnecting } =
    useDisconnectApplication(APP_NAME);
  const { openGmailAuthorizationPage } = useGmailOAuth();

  const isConnected = isApplicationConnected(APP_NAME);

  const onClickConnect = useCallback(async () => {
    try {
      openGmailAuthorizationPage();
    } catch {
      addToast(
        <ErrorToast
          description={tCommon('connectFailed', { appName: 'Gmail' })}
        />,
      );
    }
  }, [openGmailAuthorizationPage, tCommon]);

  const handleDisconnect = useCallback(async () => {
    const confirmed = await openConfirmDialog({
      title: t('disconnectModal.title'),
      message: (
        <FormattedText>
          {t.rich('disconnectModal.description', descriptionFormatRules())}
        </FormattedText>
      ),
      confirmText: t('disconnectModal.confirmText'),
      cancelText: t('disconnectModal.cancelText'),
      danger: true,
    });

    if (!confirmed) {
      return true;
    }

    try {
      await disconnectApp();
      addToast(<SuccessToast description={t('successfulDisconnected')} />);
      return true;
    } catch {
      addToast(<ErrorToast description={t('failedDisconnect')} />);
      return false;
    }
  }, [t, disconnectApp]);

  const description = (
    <FormattedText>
      {t.rich('description', descriptionFormatRules({}))}
    </FormattedText>
  );

  return (
    <AppRowLayout
      appRowId="gmail-app"
      Icon={GoogleIcon}
      title="Gmail"
      description={description}
      buttons={
        isLoadingStatus || isDisconnecting ? (
          <Button disabled>Loading...</Button>
        ) : isConnected ? (
          <DisconnectedButton
            disconnectButtonText={t('disconnectButton')}
            disconnectAction={handleDisconnect}
            tooltipCaption={t('disconnectTooltip')}
            data-testid={GMAIL_TEST_IDS.disconnectButton}
          />
        ) : (
          <Button
            onClick={onClickConnect}
            data-testid={GMAIL_TEST_IDS.connectButton}
          >
            {t('connectButton')}
          </Button>
        )
      }
    />
  );
}
